import { INode } from 'shared/types/tree'
import { NodeIdMap } from '../version_mapping/utility'
import { wrapB } from '../../ingestion/html_utility'
import { isReferenceNode, nodesOfTypeWithContent } from '../utility/TreeHelpers'
import {
  contentFromNode,
  updateNodeContent,
} from '../tree/htmlContentUpdates/utility'
import { parseHtmlString } from 'shared/utils'
import {
  appendNodeToTree,
  retrieveIds,
  updateTreeNode,
} from '../tree/TreeRequests'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { useCallback } from 'react'
import { cloneDeep } from '../../utility'

export type NodeEditResult = {
  message: string
  title: string
  operation?: () => Promise<string[] | INode>
}
export function useCopySourceNodeBeforeDestNode() {
  const copyNode = useCopyNode()

  return useCallback(
    ({
      source,
      dest,
      sourceIdMap,
      destIdMap,
    }: {
      source: INode
      dest: INode
      sourceIdMap: NodeIdMap
      destIdMap: NodeIdMap
    }): NodeEditResult => {
      const destIdString = `${dest.tree_id}:${dest.version}`
      if (
        source.parent_id === dest.parent_id &&
        source.version === dest.version
      ) {
        // This is just a reorder of child nodes
        return {
          title: 'Reordered sibling nodes',
          message: `${wrapB(source.title)} will be moved before ${wrapB(
            dest.title,
          )}`,
          operation: () =>
            reorderSiblingNodes(source, dest).then(() => [destIdString]),
        }
      }
      const { node: sourceParent } = sourceIdMap[source.parent_id]
      const { node: destParent } = destIdMap[dest.parent_id]
      const { sourceNode, siblingNode } = canCopyNode({
        sourceNode: source,
        sourceParent,
        siblingNode: dest,
        destParent,
      })
      if (sourceNode && siblingNode) {
        const { node: siblingParent } = destIdMap[siblingNode.parent_id]
        return {
          title: 'Copy node to new location',
          message: `${wrapB(
            sourceNode.title,
          )} will be copied under the node ${wrapB(
            siblingParent.title,
          )} right before ${wrapB(siblingNode.title)}`,
          operation: () =>
            copyNode({ sourceNode, siblingNode, destParent: siblingParent }),
        }
      }
      return {
        title: 'Cant Do',
        message: 'Not yet implemented',
      }
    },
    [copyNode],
  )
}

function canCopyNode({
  sourceNode,
  siblingNode,
  sourceParent,
  destParent,
}: {
  sourceNode: INode
  siblingNode: INode
  sourceParent: INode
  destParent: INode
}) {
  if (sourceParent.type === destParent.type) {
    return { sourceNode, siblingNode }
  }
  return {}
}

export function cleanTreeContent(tree: INode) {
  const nodes = nodesOfTypeWithContent({
    tree,
    types: [],
    content: ['<script'],
  })
  nodes.forEach((n) => {
    const nodeContent = contentFromNode(n)
    const cleanContent = parseHtmlString(nodeContent).body.innerHTML
    updateNodeContent(cleanContent, n)
  })
}

function filterSubtreeForAppend(subTree: INode) {
  cleanTreeContent(subTree)
  const filteredKeys = [
    'tree_id',
    'version',
    'id',
    'created_at',
    'updated_at',
    'depth',
    'parent_id',
  ]
  const children =
    subTree.nodes
      ?.filter((n) => !isReferenceNode(n))
      .map(filterSubtreeForAppend) ?? []
  const filtered: any = {
    ...subTree,
    nodes: children,
    leaves: subTree.leaves?.map((l) => {
      const lFiltered: any = { ...l }
      filteredKeys.forEach((k) => delete lFiltered[k])
      return lFiltered
    }),
  }
  filteredKeys.forEach((attr) => delete filtered[attr])
  return filtered
}

function useCopyNode() {
  const appendRequest = useWithErrorNotification(
    { errorTitle: 'Copy Node Error' },
    appendNodeToTree,
  )
  return useCallback(
    async ({
      sourceNode,
      siblingNode,
      destParent,
    }: {
      sourceNode: INode
      siblingNode: INode
      destParent: INode
    }) => {
      const cloned = cloneDeep(sourceNode)
      const filtered = filterSubtreeForAppend(cloned)
      const result = await appendRequest({
        ...retrieveIds(destParent),
        next_sibling_node_id: siblingNode.id,
        ...filtered,
      })
      return result!
    },
    [appendRequest],
  )
}

async function reorderSiblingNodes(moving: INode, thisNodeIsAfter: INode) {
  return updateTreeNode({
    ...retrieveIds(moving),
    next_sibling_node_id: thisNodeIsAfter.id,
  })
}
